/*
 * The answer search layer.
 *
 * Opens over the page, answers in a sentence, then lists. The worker decides
 * what matches and why; Shopify decides what a product costs and whether it is
 * in stock. That split is the whole design: the worker's answers live in a
 * cache for hours, and a price out of a cache is a price that is sometimes
 * wrong. So every row is fetched through Section Rendering, and nothing about
 * a product is read out of the search response.
 *
 * The timing rules are the ones predictive-search.js already settled on, for
 * the same reasons and with the same numbers: a 250ms debounce, one
 * AbortController per run so typing on cancels the request behind it, and a
 * per-query cache so backspacing to a query you already asked costs nothing.
 */

/*
 * Tippfehler.
 *
 * "macsha" ist matcha, und niemand in der Kette merkt das - der Worker nicht,
 * Shopifys eigene Suche auch nicht, beide geben auf das Wort nichts zurueck.
 * Was der Shop dagegen hat, ist seine eigene Wortliste: jedes Wort, das in
 * einem Produkttitel, einem Tag oder in den gepflegten Suchbegriffen steht. Ein
 * getipptes Wort, das darin nicht vorkommt, aber genau einem Wort daraus bis
 * auf einen Dreher gleicht, war dieses Wort.
 *
 * Das laeuft erst, wenn die Suche schon leer zurueckgekommen ist. Vorher waere
 * es gefaehrlich - "gesund" steht in keinem Produktnamen und wuerde zu
 * irgendetwas Naheliegendem gebogen. Nach einem leeren Ergebnis ist nichts mehr
 * zu verlieren.
 */

/* Fuellwoerter stehen in keinem Katalog und sind trotzdem richtig getippt. */
const ANSWER_NOISE = new Set([
  'der', 'die', 'das', 'den', 'dem', 'ein', 'eine', 'einen', 'einem', 'einer',
  'und', 'oder', 'aber', 'fuer', 'mit', 'ohne', 'von', 'zum', 'zur', 'auf',
  'aus', 'bei', 'nach', 'seit', 'ueber', 'unter', 'nicht', 'auch', 'noch',
  'schon', 'ich', 'mir', 'mich', 'mein', 'meine', 'sich', 'hab', 'habe',
  'haben', 'hat', 'bin', 'ist', 'sind', 'war', 'wird', 'werden', 'wie', 'was',
  'wann', 'warum', 'welche', 'welcher', 'welches', 'man', 'immer', 'will',
  'moechte', 'gern', 'gerne', 'etwas', 'mal', 'bitte', 'brauche', 'suche',
  'lust', 'the', 'and', 'for', 'with', 'from', 'this', 'that', 'have', 'has',
  'are', 'were', 'you', 'your', 'what', 'when', 'how', 'why', 'which', 'want',
  'need', 'looking', 'would', 'like', 'some', 'any', 'please',
]);

/* Kuerzere Woerter nicht anfassen: bei drei Buchstaben ist jedes zweite Wort
   einen Tippfehler weit weg. */
const ANSWER_CORRECT_MIN = 4;

/* Hoechstens zwei Woerter pro Frage. Wer mehr danebentippt, meint etwas
   anderes, als der Shop fuehrt. */
const ANSWER_CORRECT_MAX_WORDS = 2;

/** Kleinbuchstaben ohne Umlaute, damit "Grüntee" und "gruentee" dasselbe sind. */
function answerFold(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

function answerWords(text) {
  return answerFold(text)
    .split(/[^\p{L}\p{N}]+/u)
    .filter((word) => word.length >= ANSWER_CORRECT_MIN && !ANSWER_NOISE.has(word));
}

/**
 * Abstand zweier Woerter in Tippfehlern, inklusive Buchstabendreher.
 *
 * Bricht ab, sobald die ganze Zeile ueber der Grenze liegt: gegen eine
 * Wortliste laeuft das ein paar hundert Mal pro Suche, und die weit entfernten
 * Woerter sind die grosse Mehrheit.
 */
function answerDistance(a, b, limit) {
  if (Math.abs(a.length - b.length) > limit) return limit + 1;

  let older = [];
  let previous = [];
  for (let j = 0; j <= b.length; j++) previous[j] = j;

  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    let best = i;

    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(previous[j] + 1, row[j - 1] + 1, previous[j - 1] + cost);

      /* Der Dreher: "macsha" gegen "matcha" ist so ein Schritt, nicht zwei. */
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, older[j - 2] + 1);
      }

      row[j] = value;
      if (value < best) best = value;
    }

    if (best > limit) return limit + 1;
    older = previous;
    previous = row;
  }

  return previous[b.length];
}

/**
 * Dieselbe Frage, mit den Woertern, die gemeint waren.
 *
 * Gibt null zurueck, wenn nichts zu korrigieren ist - und auch dann, wenn zwei
 * Woerter aus dem Katalog gleich nah dran sind. Eine geratene Verwechslung ist
 * schlechter als kein Treffer: beim leeren Ergebnis sieht der Shopper, dass er
 * sich vertippt hat, bei der falschen Korrektur sieht er ein Sortiment, das er
 * nicht gesucht hat.
 */
function answerCorrect(query, vocabulary) {
  if (!vocabulary || !vocabulary.length) return null;

  const known = new Set(vocabulary);
  let changed = 0;

  const fixed = query.replace(/\p{L}+/gu, (word) => {
    if (changed >= ANSWER_CORRECT_MAX_WORDS) return word;

    const plain = answerFold(word);
    if (plain.length < ANSWER_CORRECT_MIN || known.has(plain) || ANSWER_NOISE.has(plain)) return word;

    /* Zusammengeschriebenes ist kein Tippfehler: "matchaschale" steht in keiner
       Liste und ist trotzdem richtig getippt. */
    if (vocabulary.some((entry) => entry.includes(plain) || plain.includes(entry))) return word;

    /*
     * Ab sechs Buchstaben sind zwei Tippfehler erlaubt - "macsha" ist von
     * "matcha" genau so weit weg. Zwei Fehler machen aber auch aus jedem
     * anderen Wort eines aus dem Katalog: "schuhe" wird zu "schale", "hose" zu
     * "dose". Deshalb zaehlt nicht nur der Abstand, sondern auch, wo er liegt:
     * wer sich vertippt, vertippt sich in der Mitte. Anfang und Ende bleiben
     * stehen, und daran ist der Vertipper vom anderen Wort zu unterscheiden.
     */
    const limit = plain.length >= 6 ? 2 : 1;
    const plausible = (entry, gap) =>
      gap <= 1
        ? entry[0] === plain[0]
        : entry.slice(0, 2) === plain.slice(0, 2) && entry.slice(-2) === plain.slice(-2);

    let best = null;
    let closest = limit + 1;
    let ambiguous = false;

    for (const entry of vocabulary) {
      const gap = answerDistance(plain, entry, limit);
      if (gap > limit || !plausible(entry, gap)) continue;
      if (gap < closest) {
        closest = gap;
        best = entry;
        ambiguous = false;
      } else if (gap === closest && entry !== best) {
        ambiguous = true;
      }
    }

    if (!best || ambiguous) return word;
    changed += 1;
    return best;
  });

  return changed ? fixed : null;
}

/*
 * Auswertung.
 *
 * Was mit den Antworten passiert, fuer das Dashboard des Such-Workers (/stats
 * bzw. die App "Brekky Search" im Admin). Jede Frage bekommt eine search_id,
 * die als `sid` mit zum Worker geht - der schreibt Frage und Antwort darunter
 * auf. Was danach passiert, geht als Beacon an /track. Keine Cookies, keine ID
 * ueber die Frage hinaus. Alles hier ist Beiwerk und wirft nie.
 */
const answerTrack = {
  newId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  },

  send(endpoint, shop, type, detail = {}) {
    try {
      if (!endpoint || !shop) return;
      const url = new URL('track', new URL(endpoint, window.location.origin)).toString();
      const body = JSON.stringify({ shop, type, ...detail });
      /* text/plain und als Beacon: kein OPTIONS vorab, und die Meldung geht
         auch dann noch raus, wenn der Klick die Seite gerade verlaesst. */
      if (navigator.sendBeacon?.(url, body)) return;
      fetch(url, { method: 'POST', body, keepalive: true, mode: 'no-cors' }).catch(() => {});
    } catch (error) {
      /* Zaehlen ist Beiwerk. */
    }
  },

  /*
   * Die Frage, aus der ein Produkt kam, wandert als Warenkorb-Attribut bis in
   * die Bestellung - so zaehlt auch, wer erst auf der Produktseite kauft. Der
   * Unterstrich haelt es aus dem Checkout heraus.
   */
  remember(searchId) {
    if (!searchId) return;
    try {
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _ai_search: searchId } }),
        keepalive: true,
      }).catch(() => {});
    } catch (error) {
      /* Zaehlen ist Beiwerk. */
    }
  },

  handleFromUrl(href) {
    const match = /\/products\/([^/?#]+)/.exec(href || '');
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      return match[1];
    }
  },

  /*
   * Wer aus der Suche auf ein Produkt klickt, legt es meist erst auf der
   * Produktseite in den Warenkorb - dort steht keine Antwortzeile mehr, die das
   * melden koennte. Also merkt sich der Klick Frage und Produkt fuer den Tab,
   * und das Hinzufuegen dieses Produkts zaehlt als `add` zur Frage.
   * ai-search.js schreibt denselben Schluessel.
   */
  follow(endpoint, shop, sid, handle, position) {
    if (!endpoint || !shop || !sid || !handle) return;
    try {
      sessionStorage.setItem(
        AI_SEARCH_FOLLOW_KEY,
        JSON.stringify({ endpoint, shop, sid, handle, position: position || null })
      );
    } catch (error) {
      /* Zaehlen ist Beiwerk. */
    }
  },

  added(handle) {
    try {
      const followed = JSON.parse(sessionStorage.getItem(AI_SEARCH_FOLLOW_KEY) || 'null');
      if (!followed || !handle || followed.handle !== handle) return;
      sessionStorage.removeItem(AI_SEARCH_FOLLOW_KEY);
      this.send(followed.endpoint, followed.shop, 'add', {
        sid: followed.sid,
        handle,
        position: followed.position,
        stage: 'final',
      });
    } catch (error) {
      /* Zaehlen ist Beiwerk. */
    }
  },
};

const AI_SEARCH_FOLLOW_KEY = 'ai-search-followed';

/* pubsub.js laeuft ebenfalls deferred; bis DOMContentLoaded steht es sicher. */
document.addEventListener('DOMContentLoaded', () => {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event?.source !== 'product-form') return;
    answerTrack.added(event.cartData?.handle);
  });
});

if (!customElements.get('ai-answer-search')) {
  class AiAnswerSearch extends HTMLElement {
    /** The first letter already searches — a field that ignores two keystrokes looks broken. */
    static get MIN_QUERY() {
      return 1;
    }

    /**
     * Auf dem Telefon erst ab dem Worker.
     *
     * Die Wortsuche auf ein, zwei Buchstaben traf dort oft daneben ("ic"
     * findet Hojicha) und fuellte den Platz direkt ueber der Tastatur mit
     * Beinahe-Treffern. Bis zum dritten Buchstaben bleibt der Einstieg stehen.
     * Am Desktop sucht weiter der erste Buchstabe.
     */
    static minQuery() {
      return window.matchMedia('(max-width: 899px)').matches
        ? AiAnswerSearch.WORKER_MIN_QUERY
        : AiAnswerSearch.MIN_QUERY;
    }

    /**
     * Below this the worker is not asked. One or two letters describe nothing a
     * model can answer — on "s" it wrote a sentence about smoothie bowls — but
     * they are exactly what the shop's own prefix search is for: "ma" lists
     * the matcha products at once, with no sentence and no reasons, which is
     * all two letters have earned. Same floor as the old dropdown.
     */
    static get WORKER_MIN_QUERY() {
      return 3;
    }

    /** How long the keyboard has to be still. predictive-search.js uses the same. */
    static get DEBOUNCE_MS() {
      return 250;
    }

    /*
     * Placeholder rows only appear once the wait is long enough to be noticed.
     * Below this the answer is usually already there — a cache hit comes back in
     * about a tenth of a second — and the grey rows would only flash.
     */
    static get PLACEHOLDER_DELAY_MS() {
      return 300;
    }

    /*
     * After this the answer is not worth waiting for any longer. The list still
     * is, so the layer drops the sentence and shows what the shop's own word
     * search finds. No error text: the shopper asked for products, and products
     * are what appears.
     */
    static get ANSWER_TIMEOUT_MS() {
      return 4000;
    }

    /** What the worker should write at most. Beyond this nobody scrolls. */
    static get FETCH_LIMIT() {
      return 12;
    }

    /**
     * Rows drawn before the "show the rest" bar.
     *
     * Alle, die der Worker schickt. Frueher drei auf dem Telefon und vier am
     * Desktop, der Rest hinter "Noch X Treffer ansehen" - bei sechs Treffern
     * war das ein Klick fuer drei Zeilen, den niemand machen will. Der Knopf
     * bleibt im Code und erscheint nur, wenn der Worker mehr als FETCH_LIMIT
     * schickt.
     */
    static get INITIAL_ROWS() {
      return AiAnswerSearch.FETCH_LIMIT;
    }

    static get BAR_KEY() {
      return 'BREKKY_answer_bar_hidden';
    }

    static get RECENT_KEY() {
      return 'BREKKY_answer_search_recent';
    }

    static get RECENT_MAX() {
      return 6;
    }

    /*
     * Wie lange eine fertige Antwort stehen muss, bis sie als gesehen zaehlt.
     * Getippt wird hier ab drei Buchstaben und nach 250ms Pause - "mat",
     * "matc" und "match" kommen alle fertig an, gelesen wird aber nur das,
     * wobei jemand stehen bleibt.
     */
    static get SEEN_AFTER_MS() {
      return 1500;
    }

    constructor() {
      super();

      this.onInput = this.handleInput.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onOpenerClick = this.handleOpenerClick.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);

      /** query -> the worker's parsed answer. Survives as long as the page does. */
      this.cache = new Map();
      /** handle -> the rendered row's HTML, so a repeated product is not refetched. */
      this.rowCache = new Map();

      this.controller = null;
      this.timer = null;
      this.placeholderTimer = null;
      this.query = '';
      this.opener = null;
      this.remaining = [];
    }

    connectedCallback() {
      this.onResize = () => this.measureHeader();
      window.addEventListener('resize', this.onResize);

      this.input = this.querySelector('[data-input]');
      this.results = this.querySelector('[data-results]');
      this.panels = this.querySelector('[data-panels]');
      this.recentBox = this.querySelector('[data-recent-box]');
      this.recentList = this.querySelector('[data-recent]');

      /*
       * Every fixed string the JS writes comes from the locale, through one JSON
       * block in the section. Twelve data attributes would say the same thing
       * twelve times, and a German sentence in this file would be a string the
       * translation editor cannot reach.
       */
      /* Die beliebten Produkte, so wie Liquid sie gerendert hat. Der Block wird
         unter einer Serviceantwort noch einmal gebraucht, und dort ist eine
         fertige Zeile mit funktionierendem Warenkorb-Knopf mehr wert als eine
         zweite Runde Section Rendering. */
      this.popularHtml = this.querySelector('[data-popular]')?.outerHTML || '';

      this.strings = {};
      this.flags = [];
      try {
        const source = this.querySelector('[data-flags]');
        if (source) this.flags = JSON.parse(source.textContent) || [];
      } catch (error) {
        console.warn('answer search: flags unreadable', error);
      }
      this.loadServiceFlags();
      try {
        const source = this.querySelector('[data-strings]');
        if (source) this.strings = JSON.parse(source.textContent);
      } catch (error) {
        console.warn('answer search: strings unreadable', error);
      }

      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      if (this.input) {
        this.input.addEventListener('input', this.onInput);
      }
      document.addEventListener('keydown', this.onKeydown);
      /*
       * Capture phase, so this runs before details-modal.js's own summary
       * handler and the old dropdown never gets as far as opening. Anything
       * carrying data-answer-search-open opens the layer — the header's search
       * button today, whatever else wants to tomorrow.
       */
      document.addEventListener('click', this.onOpenerClick, true);

      this.renderRecent();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onOpenerClick, true);
      this.cancel();
      this.release();
    }

    /* ---------------------------------------------------------------- open */

    /**
     * Wo der Header anfaengt und wie hoch seine eigene Zeile ist.
     *
     * Die 88px der Leiste standen aus dem Entwurf im Stylesheet. Der Shop hat
     * seinen Header aber woanders - Logohoehe, Padding und die Ankuendigungs-
     * balken darueber sind Einstellungen, und der Balken ist obendrein je nach
     * Aktion mal eine und mal zwei Zeilen hoch. Beim Oeffnen sprang die Seite
     * deshalb. Also gemessen statt aufgeschrieben: die Leiste wird so hoch wie
     * die Headerzeile, und die Suche faengt darueber gar nicht erst an, damit
     * die Ankuendigung stehen bleibt.
     */
    measureHeader() {
      const section = document.querySelector('.section-header');
      if (!section) return;

      const rect = section.getBoundingClientRect();
      if (rect.height === 0) return;

      /* Der Abschnitt und nicht die Zeile darin: er enthaelt hier nur den
         Header, und seine Hoehe schliesst dessen Rahmen mit ein - genau das,
         was die Leiste treffen soll. Die Kategorienleiste steht ausserhalb. */
      this.style.setProperty('--answer-search-top', `${Math.max(0, Math.round(rect.top))}px`);
      this.style.setProperty('--answer-search-bar-height', `${Math.round(rect.height)}px`);

      /*
       * Auf dem Telefon legt sich die Schicht nicht vor den Header, sondern
       * haengt darunter: Burger, Logo und Warenkorb bleiben bedienbar, und das
       * Feld sitzt genau dort, wo die Suchleiste des Shops sitzt. Gemessen wird
       * deren Oberkante und ihre Hoehe; ohne Leiste - der Fall auf dem Desktop
       * - bleiben beide Werte die des Abschnitts, und alles ist wie vorher.
       */
      /* Im Dokument, nicht im Abschnitt: die Leiste steht bewusst ausserhalb
         des Sticky-Wrappers, damit sie beim Scrollen wegfaehrt. */
      const bar = document.querySelector('[data-answer-bar]');
      const barRect = bar && bar.offsetParent ? bar.getBoundingClientRect() : null;

      /*
       * Wo die Schicht ansetzt: an der Oberkante der Leiste, solange die zu
       * sehen ist. Ist weggescrollt worden, steht sie ausserhalb des Bildes -
       * dann faengt die Schicht unter dem Header an, der oben klebt, statt ihn
       * mit abzudecken.
       */
      const ansatz = barRect && barRect.top > 0 ? barRect.top : rect.bottom;

      this.style.setProperty(
        '--answer-search-bar-top',
        `${Math.max(0, Math.round(barRect ? ansatz : rect.top))}px`
      );
      this.style.setProperty(
        '--answer-search-bar-own-height',
        `${Math.round(barRect ? barRect.height : rect.height)}px`
      );
    }

    open(opener) {
      if (this.hasAttribute('data-show')) return;

      this.opener = opener || null;
      this.measureHeader();
      this.setAttribute('data-show', '');

      /*
       * The page behind the layer is closed off rather than merely covered: a
       * Tab press that walks into it leaves a shopper typing into a field they
       * cannot see. `inert` takes it out of both the tab order and the
       * accessibility tree in one attribute.
       */
      // Not `this`: Section Rendering wraps the element in its own div, so the
      // thing to spare is whichever body child this sits inside. Marking the
      // wrapper inert made the layer inert with it — nothing in it focusable,
      // the field included.
      let own = this;
      while (own.parentElement && own.parentElement !== document.body) own = own.parentElement;

      /*
       * Auf dem Telefon bleibt der Header bedienbar.
       *
       * Dort legt sich die Schicht nicht darueber, sondern haengt darunter -
       * Burger, Logo und Warenkorb sind weiter zu sehen. Inert gestellt waren
       * sie zu sehen und tot, und nichts sagte warum. Auf dem Desktop deckt die
       * Schicht den Header ab, da bleibt er stillgelegt.
       */
      const kopf = window.matchMedia('(max-width: 899px)').matches
        ? [...document.body.children].find(
            /* matches und nicht nur querySelector: der Header-Abschnitt ist
               hier selbst ein Kind von body, kein Enkel. */
            (node) => node.matches?.('.section-header') || node.querySelector?.('.section-header')
          )
        : null;

      this.siblings = [...document.body.children].filter((node) => node !== own && node !== kopf);
      this.siblings.forEach((node) => node.setAttribute('inert', ''));

      this.scrollY = window.scrollY;
      /*
       * data-answer-search-active, and not the opener's data-answer-search-open:
       * the opener lookup walks up with closest(), and with the same name on
       * <body> every click inside the open layer found <body>, was treated as
       * "open the layer", and was stopped there — the filters, the chips and the
       * cart buttons were all dead and nothing said why.
       */
      document.body.setAttribute('data-answer-search-active', '');

      this.renderRecent();

      /*
       * The layer starts at visibility:hidden and a hidden element cannot take
       * focus. Reading a layout property forces the style with data-show to be
       * applied before the next line runs — visibility flips on that attribute
       * without a delay — so the field is focusable right here. Not
       * requestAnimationFrame: that never fires in a throttled tab, and the
       * field then sat unfocused while the shopper started typing into nothing.
       */
      if (this.input) {
        void this.offsetHeight;
        this.input.focus();
      }
    }

    close() {
      if (!this.hasAttribute('data-show')) return;

      this.cancel();
      this.removeAttribute('data-show');
      this.release();
      this.lastLocal = null;

      // Back to where the shopper was, and back to the control they left from,
      // so closing the layer does not also lose their place on the page.
      if (this.opener && document.contains(this.opener)) this.opener.focus();
      this.opener = null;
    }

    release() {
      if (this.siblings) {
        this.siblings.forEach((node) => node.removeAttribute('inert'));
        this.siblings = null;
      }
      document.body.removeAttribute('data-answer-search-active');
    }

    /* -------------------------------------------------------------- typing */

    handleInput() {
      const value = this.input.value.trim();
      // data-typed drives the "Leeren" button only. What the layer shows is
      // data-active, and that flips on the first real search and off on an
      // empty field — never in between. With one or two letters typed the
      // empty state (or the last answer) stays: hiding it on the first
      // keystroke and searching on the third left two blank screens in
      // between, and a shopper typing "ma" looked at nothing.
      this.toggleAttribute('data-typed', value.length > 0);
      if (value.length === 0) {
        this.removeAttribute('data-active');
        this.removeAttribute('data-kind');
        this.results.innerHTML = '';
        this.panels.innerHTML = '';
      }

      if (value === this.query) return;
      this.query = value;

      this.cancel();

      if (value.length < AiAnswerSearch.minQuery()) return;

      // A query already answered needs no debounce — waiting a quarter second to
      // show something that is already in memory is a quarter second of nothing.
      if (this.cache.has(this.cacheKey(value, true))) {
        this.run(value);
        return;
      }

      this.timer = setTimeout(() => {
        /* Abgelaufen heisst: nichts wartet mehr. handleEnter liest daran ab,
           ob die Frage noch aussteht. */
        this.timer = null;
        this.run(value);
      }, AiAnswerSearch.DEBOUNCE_MS);
    }

    cancel() {
      /* Abgebrochen ist nicht beantwortet: Enter darf die Frage danach wieder
         stellen. */
      this.asked = null;
      clearTimeout(this.timer);
      clearTimeout(this.placeholderTimer);
      this.timer = null;
      this.placeholderTimer = null;
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
      this.results.removeAttribute('data-loading');
      this.removeAttribute('data-loading');
    }

    cacheKey(query, correct) {
      return `${correct ? 'fixed' : 'literal'}:${query.toLowerCase()}`;
    }

    /**
     * Zurueck an den Anfang der Liste, auf die harte Tour, weil Safari auf dem
     * iPhone scrollTo() und auch ein schlichtes scrollTop = 0 ignoriert hat:
     * - Eine Liste, die nach einem Wisch noch nachlaeuft, ueberschreibt jede
     *   gesetzte Position. Overflow fuer einen Frame aus stoppt das Nachlaufen,
     *   danach haelt die Position.
     * - Mit offener Tastatur verschiebt das iPhone manchmal die Seite statt
     *   der Liste. Die Seite geht zurueck auf die Position beim Oeffnen.
     */
    scrollToTop() {
      const body = this.querySelector('.answer-search__body');
      if (body && body.scrollTop !== 0) {
        body.style.overflowY = 'hidden';
        body.scrollTop = 0;
        requestAnimationFrame(() => {
          body.scrollTop = 0;
          body.style.overflowY = '';
        });
      }

      if (typeof this.scrollY === 'number' && window.scrollY !== this.scrollY) {
        window.scrollTo(0, this.scrollY);
      }
    }

    /* -------------------------------------------------------------- search */

    /**
     * Ob die Frage eine ist, auf die eine Seite antwortet und keine Liste.
     *
     * Eine Zeile bringt mehrere Begriffe mit, kommagetrennt. Der Begriff muss
     * als Wort im Satz stehen, nicht irgendwo darin - sonst faengt "paket"
     * auch das "Probierpaket".
     */
    matchingFlag(query) {
      const haystack = ` ${query.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim()} `;

      return this.flags.find((flag) =>
        String(flag.match || '')
          .split(',')
          .map((word) => word.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim())
          .filter(Boolean)
          .some((word) => haystack.includes(` ${word} `))
      );
    }

    /*
     * Service-Antworten aus der App (Seite "Service"): dieselbe Form wie die
     * Hinweise oben, gepflegt vom Shop statt im Theme-Editor. Sie stehen vor
     * denen aus dem Theme, damit eine Aenderung in der App gewinnt. Einmal je
     * Sitzung geholt, zehn Minuten gemerkt - sie aendern sich selten, und
     * ohne sie sucht die Suche einfach wie bisher.
     */
    async loadServiceFlags() {
      const { endpoint, shop } = this.dataset;
      if (!endpoint || !shop) return;
      const key = `ai-service:${shop}`;
      const use = (flags) => {
        if (Array.isArray(flags) && flags.length) this.flags = flags.concat(this.flags);
      };

      try {
        const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (cached && Date.now() - cached.at < 10 * 60 * 1000) return use(cached.flags);
      } catch (error) {
        /* Ohne Speicher eben jedes Mal holen. */
      }

      try {
        const url = new URL('service', new URL(endpoint, window.location.origin));
        url.searchParams.set('shop', shop);
        const response = await fetch(url, { credentials: 'omit' });
        if (!response.ok) return;
        const flags = (await response.json()).flags || [];
        use(flags);
        try {
          sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), flags }));
        } catch (error) {
          /* Dann eben beim naechsten Laden noch einmal. */
        }
      } catch (error) {
        console.warn('answer search: service answers unavailable', error);
      }
    }

    renderFlag(flag) {
      clearTimeout(this.placeholderTimer);
      this.results.removeAttribute('data-loading');
      this.removeAttribute('data-loading');
      this.renderedHandles = '';

      this.results.innerHTML = `
        <div class="answer-search__answer answer-search__answer--flag" role="alert">
          <h2 class="answer-search__headline">${this.escape(flag.heading || '')}</h2>
          <p class="answer-search__text">${this.escape(flag.text || '')}</p>
          ${
            flag.link
              ? `<a class="answer-search__flag-link" href="${this.escape(flag.link.url)}">${this.escape(
                  flag.link.text
                )}</a>`
              : ''
          }
        </div>
        ${this.popularHtml}`;
      this.panels.innerHTML = '';
    }

    async run(query, options) {
      const correct = !options || options.correct !== false;
      const key = this.cacheKey(query, correct);
      this.setAttribute('data-active', '');
      this.asked = options?.correctedFrom || query;

      /* Eine neue Frage faengt oben an. Wer unten auf einen Vorschlag tippte,
         sah sonst zuerst das Ende der neuen Antwort. */
      this.scrollToTop();

      /* A new question, so nothing on screen counts as already drawn. */
      this.renderedHandles = '';

      /* Vor allem anderen: eine Serviceanfrage beantwortet eine Seite, keine
         Liste - und sie muss nicht auf den Worker warten. */
      const flag = this.matchingFlag(query);
      if (flag) {
        this.rememberQuery(query);
        this.trackLocal(query);
        this.renderFlag(flag);
        return;
      }

      /* Fuer die Auswertung: wie gefragt wurde, und welche Frage gerade gilt. */
      this.via = options?.via || 'typed';
      this.retryOf = options?.retryOf || null;
      this.searchId = null;

      this.controller = new AbortController();
      const { signal } = this.controller;

      /*
       * The old list stays and dims instead of being emptied. Typing on should
       * not make the page jump, and an empty box under a half-typed word says
       * "nothing found" when nothing has been asked yet.
       */
      this.results.setAttribute('data-loading', '');
      this.setAttribute('data-loading', '');
      /*
       * Past the delay the canvas's loading state takes over from the dimmed
       * list: the progress line, the answer box as a sketch with "Ich lese
       * gerade 93 Produkte …", and three rows drawn as the shapes of rows. A
       * cache hit lands long before this fires and nothing is redrawn.
       */
      this.placeholderTimer = setTimeout(() => {
        if (!signal.aborted) this.renderSkeleton();
      }, AiAnswerSearch.PLACEHOLDER_DELAY_MS);

      let data = this.cache.get(key);

      if (!data && query.length < AiAnswerSearch.WORKER_MIN_QUERY) {
        // Too short for the worker, see WORKER_MIN_QUERY: the shop's own
        // prefix search answers, and its list is cached like an answer.
        data = await this.fallback(query, signal).catch(() => null);
        if (data) this.cache.set(key, data);
      } else if (!data) {
        try {
          /*
           * The list is drawn the moment the worker has settled the handles,
           * roughly a second before the sentence above it exists. Until then it
           * is the skeleton; after it, only the answer box changes.
           */
          data = await this.fetchAnswer(query, correct, signal, (partial) => {
            if (signal.aborted) return;
            /*
             * Niemand wartet hier auf das Ergebnis - der Stream laeuft weiter,
             * waehrend die Zeilen geholt werden. Tippt jemand in dem Moment
             * weiter, brechen diese Anfragen ab, und ohne das catch waere das
             * eine unbehandelte Rejection statt eines abgebrochenen Rendervorgangs.
             */
            this.render(partial, signal, { pending: true }).catch((error) => {
              if (error?.name !== 'AbortError' && !signal.aborted) {
                console.warn('answer search: partial render failed', error);
              }
            });
          });
          /* Ein abgelaufener Teiltreffer ist kein Ergebnis zum Behalten - beim
             naechsten Mal soll wieder der ganze Worker gefragt werden, nicht
             fuer immer die Liste ohne Satz aus dem Cache kommen. */
          if (!data.timedOut) this.cache.set(key, data);
        } catch (error) {
          if (signal.aborted) return;
          this.track('error', { sid: this.searchId });
          // The answer is gone; the products need not be. The shop's own word
          // search is what is left, and it is a correct answer to the question.
          data = await this.fallback(query, signal).catch(() => null);
        }
      }

      if (signal.aborted) return;
      clearTimeout(this.placeholderTimer);
      this.results.removeAttribute('data-loading');
      this.removeAttribute('data-loading');

      /*
       * Nichts gefunden, und zwar egal ob der Worker leer zurueckkam oder gar
       * nicht antwortete: das ist der Moment fuer den Vertipper. Einmal, und
       * nur wenn nicht schon korrigiert wurde - die korrigierte Frage steht
       * danach vollstaendig in der Wortliste und ergaebe dieselbe Runde noch
       * einmal.
       */
      const leer = !data || !(Array.isArray(data.products) ? data.products.length : 0);
      if (leer && correct && !options?.correctedFrom && (await this.retryCorrected(query))) return;

      if (!data) {
        this.renderEmpty(query);
        return;
      }

      /* Der Worker kennt die korrigierte Frage, nicht die getippte. Gespeichert
         wird die getippte: sie ist es, die der Kunde wiedererkennt. */
      if (options?.correctedFrom) data = { ...data, corrected_from: options.correctedFrom };

      /* Aus dem Cache dieser Seite kommt die search_id der ersten Frage mit:
         dieselbe Frage zweimal ist dieselbe Suche, der Worker sah sie einmal. */
      this.searchId = data.searchId || null;

      this.rememberQuery(options?.correctedFrom || query);
      await this.render(data, signal);
    }

    /**
     * Die Wortliste des Shops, einmal geholt und dann behalten.
     *
     * Eine eigene Section ueber die Section Rendering API: auf jeder Seite
     * mitzuliefern waere sie ein paar Kilobyte fuer einen Fall, der selten
     * eintritt, und hier kostet sie erst etwas, wenn er eingetreten ist.
     */
    async vocabulary() {
      if (this.words) return this.words;
      if (this.wordsRequest) return this.wordsRequest;

      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}?section_id=ai-answer-search-words`, window.location.origin);

      this.wordsRequest = fetch(url.toString())
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const node = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-answer-words]');
          const text = node ? JSON.parse(node.textContent) : '';
          this.words = [...new Set(answerWords(text))];
          return this.words;
        })
        .catch((error) => {
          console.warn('answer search: vocabulary could not be read', error);
          this.words = [];
          return this.words;
        });

      return this.wordsRequest;
    }

    /**
     * Zweiter Anlauf mit dem Wort, das gemeint war.
     *
     * Ohne Hinweis darauf, dass korrigiert wurde: die Korrekturzeile stand hier
     * einmal und erklaerte dem Kunden seinen Vertipper, statt ihm sein Produkt
     * zu zeigen. Findet auch die korrigierte Frage nichts, bleibt es bei der
     * leeren Antwort auf die, die wirklich getippt wurde.
     */
    async retryCorrected(query) {
      let fixed = null;
      try {
        fixed = answerCorrect(query, await this.vocabulary());
      } catch (error) {
        return false;
      }

      if (!fixed || answerFold(fixed) === answerFold(query)) return false;
      if (this.query !== query) return false;

      await this.run(fixed, { correctedFrom: query, via: 'retry', retryOf: this.searchId });
      return true;
    }

    buildUrl(query, correct, stream) {
      const url = new URL(this.dataset.endpoint);
      url.searchParams.set('query', query);
      url.searchParams.set('shop', this.dataset.shop);
      url.searchParams.set('limit', String(AiAnswerSearch.FETCH_LIMIT));
      url.searchParams.set('answer', '1');
      /*
       * The handles stand in the model's JSON before the sentence does. With
       * stream=1 the worker sends them as soon as they are settled and the
       * finished answer after them, so the list can be on screen while the
       * sentence above it is still being written.
       */
      if (stream) url.searchParams.set('stream', '1');
      if (!correct) url.searchParams.set('correct', '0');
      if (this.dataset.language) url.searchParams.set('language', this.dataset.language);
      /* Nur fuer die Auswertung - der Cache des Workers sieht diese Parameter nicht. */
      if (this.searchId) url.searchParams.set('sid', this.searchId);
      url.searchParams.set('src', 'overlay');
      if (this.via) url.searchParams.set('via', this.via);
      if (this.retryOf) url.searchParams.set('retry_of', this.retryOf);
      url.searchParams.set('path', window.location.pathname);
      return url.toString();
    }

    /**
     * One ndjson line per state, read as it arrives.
     *
     * A network cuts in the middle of text, so bytes are collected until a
     * newline; whatever follows is the beginning of the next line and stays
     * put. `partial` carries the handles without the sentence, `final` the same
     * list with it. A worker that answers in plain JSON - an older deploy, a
     * cache hit - is read as before.
     */
    async readStream(response, onPartial) {
      const streamed = (response.headers.get('Content-Type') || '').includes('ndjson');
      if (!streamed || !response.body) return response.json();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let last = null;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let cut = buffer.indexOf('\n');
        while (cut !== -1) {
          const line = buffer.slice(0, cut).trim();
          buffer = buffer.slice(cut + 1);
          cut = buffer.indexOf('\n');
          if (!line) continue;

          let event;
          try {
            event = JSON.parse(line);
          } catch {
            continue;
          }

          if (event.type === 'error') throw new Error(event.payload?.error || 'search worker failed');
          if (event.type === 'partial') {
            if (Array.isArray(event.payload?.products)) onPartial(event.payload);
            continue;
          }
          last = event.payload;
        }
      }

      if (!last) throw new Error('search worker returned no answer');
      return last;
    }

    async fetchAnswer(query, correct, signal, onPartial) {
      const searchId = answerTrack.newId();
      this.searchId = searchId;
      /*
       * The four-second rule from the brief, as its own timer rather than the
       * request's: aborting the fetch would also throw away the handles, and it
       * is only the sentence that has run out of patience. Whichever finishes
       * first wins, and a late answer is simply ignored.
       *
       * Sobald der Stream die Handles gebracht hat, scheitert der Timer nicht
       * mehr, sondern gibt genau die zurueck. Ein Fehler schickt `run` in die
       * Wortsuche des Shops, und deren Liste wuerde die Zeilen ersetzen, die
       * der Kunde in dem Moment schon liest - der ausgebliebene Satz kostete
       * so die Antwort, zu der er gehoert. Dieselben Zeilen, nur ohne Satz:
       * `render` zieht dann den Platzhalter der Antwort ersatzlos heraus.
       */
      let partial = null;
      const timeout = new Promise((resolve, reject) =>
        setTimeout(() => {
          if (partial) resolve({ ...partial, timedOut: true });
          else reject(new Error('answer timeout'));
        }, AiAnswerSearch.ANSWER_TIMEOUT_MS)
      );

      const request = fetch(this.buildUrl(query, correct, !!onPartial), { signal }).then(
        async (response) => {
          // 404 is the worker saying nothing matched. That is an answer.
          if (!response.ok && response.status !== 404) {
            throw new Error(`search worker responded ${response.status}`);
          }
          if (!onPartial) return { ...(await response.json()), searchId };

          const last = await this.readStream(response, (payload) => {
            partial = { ...payload, searchId };
            onPartial(partial);
          });
          return { ...last, searchId };
        }
      );

      return Promise.race([request, timeout]);
    }

    /**
     * Shopify's own suggestions, shaped like the worker's answer so everything
     * downstream stays the same. No sentence, no reasons, no groups — those are
     * the things the worker knows and this does not.
     */
    async fallback(query, signal) {
      const url = new URL(`${window.Shopify?.routes?.root || '/'}search/suggest.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', String(AiAnswerSearch.FETCH_LIMIT));

      const response = await fetch(url.toString(), { signal });
      if (!response.ok) throw new Error(`suggest responded ${response.status}`);

      const body = await response.json();
      const products = body?.resources?.results?.products || [];
      return {
        query,
        kind: 'term',
        corrected_from: null,
        answer: null,
        products: products.map((product) => ({ handle: product.handle, reasons: [] })),
        groups: [],
        facets: [],
        related: [],
        articles: [],
        total: products.length,
      };
    }

    /* -------------------------------------------------------------- render */

    async render(data, signal, options) {
      const products = Array.isArray(data.products) ? data.products : [];
      const pending = !!options?.pending;

      if (!products.length) {
        if (pending) return;
        this.results.innerHTML = '';
        this.panels.innerHTML = '';
        this.renderEmpty(data.query || this.query);
        this.track('empty', { sid: data.searchId });
        return;
      }

      /*
       * The finished answer almost always names the products the partial did -
       * it is the same list, written out. Redrawing it would fetch every row
       * again and make the arriving sentence look like a second search, so the
       * rows stay and only the block above them and the reasons change.
       */
      const key = products.map((product) => product.handle).join(' ');
      if (!pending && key && key === this.renderedHandles) {
        this.updateAnswer(data);
        this.trackShown(data);
        return;
      }
      this.renderedHandles = key;
      this.shared = pending ? null : this.sharedReason(products);

      clearTimeout(this.placeholderTimer);
      this.results.removeAttribute('data-loading');
      this.removeAttribute('data-loading');

      this.filter = null;
      this.setAttribute('data-kind', data.kind || 'term');

      /*
       * The chips are rendered for every kind and the stylesheet decides where
       * they show: above the list on a single word, and on a phone for every
       * kind, where the right-hand column has become this row. On the desktop
       * answer states the same values sit in the "Verfeinern" box instead.
       */
      const parts = [];
      /*
       * While the handles are there and the sentence is not, the box stands as
       * an outline in its place - same spot, same height, so the rows below do
       * not jump when the sentence lands.
       */
      if (data.answer) parts.push(this.answerHtml(data));
      else if (pending) parts.push(this.answerBonesHtml());
      if (data.groups?.length) parts.push(this.chipsHtml(data.groups));
      parts.push('<div class="answer-search__list" data-answer-list></div>');

      this.results.innerHTML = parts.join('');
      this.panels.innerHTML = this.panelsHtml(data);

      /*
       * Der Filter, den der Worker mitschickt, wird nicht mehr vorgehakt.
       *
       * Er haelt genau das zurueck, was diese Suche interessant macht: auf "ich
       * will matcha" kamen elf Produkte, darunter eine Smoothie Bowl mit
       * Matcha-Geschmack und ein Matcha-Proteinriegel - und der vorgewaehlte
       * Haken "Matcha" hat die vier Querschlaeger weggeblendet. Uebrig blieb
       * die Liste, die man auch ueber die Kategorie gefunden haette.
       *
       * Die Kaesten rechts bleiben, mit ihren Zahlen. Wer eingrenzen will,
       * grenzt ein; wer fragt, bekommt zuerst die ganze Antwort.
       */
      const list = this.results.querySelector('[data-answer-list]');
      const initial = AiAnswerSearch.INITIAL_ROWS;
      const first = products.slice(0, initial);
      this.remaining = products.slice(initial);

      await this.appendRows(list, first, signal);
      if (signal.aborted) return;
      /* Noch einmal, sobald die Zeilen stehen: auf dem iPhone kann die
         Tastatur die Liste in der Zwischenzeit verschoben haben. */
      this.scrollToTop();

      if (this.remaining.length) {
        list.insertAdjacentHTML('afterend', this.moreHtml(this.remaining.length, data.total));
      }

      if (!pending) this.trackShown(data);
    }

    async appendRows(list, products, signal) {
      const html = await Promise.all(products.map((product) => this.rowHtml(product.handle, signal)));
      if (signal.aborted) return;

      products.forEach((product, i) => {
        if (!html[i]) return;
        list.insertAdjacentHTML('beforeend', html[i]);
        const row = list.lastElementChild;
        this.decorate(row, product, this.shared);
      });
      this.sinkSoldOut(list);
      this.applyFilter();
    }

    /**
     * Was ausverkauft ist, rutscht ans Ende.
     *
     * Der Worker sortiert nach Passgenauigkeit und weiss nichts ueber den
     * Bestand - der steht erst in der Zeile, die Liquid rendert. Drei Kids
     * Bowls hintereinander, die erste und die dritte ausverkauft, sind eine
     * Antwort, in der man zweimal ins Leere greift, bevor man etwas kaufen
     * kann. Die Reihenfolge innerhalb der beiden Gruppen bleibt die des
     * Workers.
     *
     * Laeuft nach jedem Nachladen erneut, also auch, wenn "Noch X Treffer
     * ansehen" weitere Zeilen anhaengt.
     */
    sinkSoldOut(list) {
      if (!list) return;

      for (const row of [...list.children]) {
        if (row.querySelector('[data-unavailable]')) list.appendChild(row);
      }
    }

    /**
     * Narrows the list to one group, client-side. The worker already said which
     * groups each product is in, so no second request — and the count on the
     * chip counts the same memberships this hides by, which is what keeps chip
     * and list agreeing.
     */
    applyFilter() {
      const value = this.filter;
      this.results.querySelectorAll('[data-answer-row]').forEach((row) => {
        const groups = (row.dataset.groups || '').split(' ');
        row.toggleAttribute('data-filtered', !!value && !groups.includes(value));
      });
      this.querySelectorAll('[data-group], [data-facet]').forEach((control) => {
        const own = control.dataset.group || control.dataset.facet;
        control.toggleAttribute('data-active', !!value && own === value);
      });
    }

    async toggleFilter(value) {
      this.filter = this.filter === value ? null : value;

      /*
       * A filter over four visible rows and ten hidden ones would show a count
       * the list cannot match. The rest are fetched first, then hidden — so
       * "Proteinriegel 5" means five rows, not "five, of which you can see
       * two".
       */
      const more = this.results.querySelector('[data-more]');
      if (this.filter && more) await this.showRest(more);

      this.applyFilter();
    }

    /**
     * The row itself, straight out of the theme. One request per handle, because
     * ?section_id= only ever renders one section — the same trade-off
     * ai-search.js already makes, and the responses are cacheable by the CDN.
     */
    async rowHtml(handle, signal) {
      if (this.rowCache.has(handle)) return this.rowCache.get(handle);

      try {
        const response = await fetch(
          `${window.Shopify?.routes?.root || '/'}products/${handle}?section_id=${this.dataset.rowSection}`,
          { signal }
        );
        if (!response.ok) return null;

        const text = await response.text();
        // Section Rendering wraps the section in its own div; the row is what we
        // want to place, not the wrapper.
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const row = parsed.querySelector('[data-answer-row]');
        const html = row ? row.outerHTML : null;

        if (html) this.rowCache.set(handle, html);
        return html;
      } catch (error) {
        if (error?.name !== 'AbortError') console.warn('answer search: row failed', handle, error);
        return null;
      }
    }

    /**
     * Die eine Begruendung, die der Worker fuer die ganze Frage geschrieben hat
     * - wenn es denn eine ist.
     *
     * Die Antwort traegt eine Begruendung je Produkt, aber das Modell fuellt
     * sie alle mit demselben Satz: die Frage zurueckgelesen, nicht ein Urteil
     * ueber eines der Produkte. Einmal gelesen ist sie nuetzlich, unter zwoelf
     * Zeilen ist sie Rauschen - und sie kann nicht sagen, welche der zwoelf
     * man nehmen soll, was die ganze Aufgabe dieser Zeile waere.
     */
    sharedReason(products) {
      /*
       * Verglichen wird, was auf der Zeile landen wuerde, nicht was der Worker
       * geschickt hat.
       *
       * Die zweite Begruendung ist die Collection, und die faellt beim Rendern
       * ohnehin weg, weil sie schon als Rubrik ueber dem Titel steht. Nimmt man
       * sie in den Vergleich, sind vier Zeilen "verschieden", die alle sichtbar
       * dasselbe sagen - auf "was esse ich nach dem sport" stand viermal
       * "passt: High-Protein" untereinander.
       */
      const lines = products.map((product) =>
        (product.reasons || [])
          .filter((reason, index) => reason && index !== 1)
          .join(' · ')
          .trim()
      );

      if (lines.length < 2 || lines.some((line) => !line)) return null;
      if (!lines.every((line) => line === lines[0])) return null;

      /*
       * Der Worker stellt "Treffer:" davor, und die Zeile traegt oft zweimal
       * dasselbe, weil die zweite Begruendung die Collection ist und die auf
       * einem Begriff wie "proteinriegel" genauso heisst. Ueber der Antwort
       * steht das Label schon davor, also faellt sein Wort weg und was doppelt
       * ist, faellt zusammen: aus "Treffer: Proteinriegel · Proteinriegel" wird
       * "Proteinriegel".
       */
      const teile = lines[0]
        .replace(/^[^:]{1,14}:\s*/, '')
        .split('·')
        .map((teil) => teil.trim())
        .filter(Boolean);

      /* "Smoothie Bowl · Smoothie Bowls" ist einmal dasselbe: steckt der eine
         Begriff im anderen, bleibt der laengere. */
      const eindeutig = [...new Set(teile)].filter(
        (teil, _, alle) =>
          !alle.some((anderer) => anderer !== teil && anderer.toLowerCase().includes(teil.toLowerCase()))
      );

      return eindeutig.length ? eindeutig.join(' · ') : null;
    }

    /**
     * What only the worker knows goes onto the row here: why it is in the list,
     * and which shelf it belongs on. The category Liquid rendered is the
     * product type, which on this catalog is sometimes a tax category.
     */
    decorate(row, product, shared) {
      const reasons = row.querySelector('[data-reasons]');
      if (reasons && Array.isArray(product.reasons)) {
        /* Steht sie schon ueber der Antwort, gehoert sie nicht unter jede
           Zeile. */
        /*
         * Die zweite Begruendung ist die Collection, und die steht gleich
         * darunter schon als Rubrik ueber dem Titel. Als Pille daneben stand
         * "Matcha" dann dreimal in einer Zeile: als Rubrik, als "passt: matcha"
         * und noch einmal als Pille.
         */
        const gezeigt = shared
          ? []
          : product.reasons.filter((reason, index) => reason && index !== 1);

        reasons.innerHTML = gezeigt
          .map((reason) => `<span class="answer-row__reason">${this.escape(reason)}</span>`)
          .join('');
      }

      // The second reason is the collection the worker matched. It is a better
      // shelf name than the product type, so it takes that line over.
      const group = product.reasons?.[1];
      const type = row.querySelector('[data-type]');
      if (type && group) type.textContent = group;

      row.dataset.groups = Array.isArray(product.groups) ? product.groups.join(' ') : '';
    }

    /**
     * Swaps in what the partial did not have: the sentence, the chips and the
     * reason per row. The list itself is left alone.
     */
    updateAnswer(data) {
      this.shared = this.sharedReason(data.products || []);
      this.removeAttribute('data-loading');
      this.results.removeAttribute('data-loading');
      clearTimeout(this.placeholderTimer);

      const bones = this.results.querySelector('[data-answer-bones]');
      const parts = [];
      if (data.answer) parts.push(this.answerHtml(data));
      if (data.groups?.length) parts.push(this.chipsHtml(data.groups));

      /*
       * Die Chipreihe steht hier schon, seit das Teilergebnis die Zeilen
       * gebracht hat - ersetzt wird aber nur der Platzhalter der Antwort. Ohne
       * das hier stand sie danach zweimal untereinander, einmal aus dem
       * Teilergebnis und einmal aus dem fertigen.
       */
      this.results.querySelector('[data-group-row]')?.remove();

      if (bones) bones.outerHTML = parts.join('');
      else if (parts.length) this.results.insertAdjacentHTML('afterbegin', parts.join(''));

      this.panels.innerHTML = this.panelsHtml(data);

      for (const product of data.products || []) {
        const row = this.results.querySelector(`[data-answer-row][data-handle="${CSS.escape(product.handle)}"]`);
        if (row) this.decorate(row, product, this.shared);
      }
      this.applyFilter();
    }

    /*
     * The answer box while it is still being written - the same sketch the
     * loading state draws, so the box does not change shape when the list
     * arrives under it and the sentence a moment later.
     */
    answerBonesHtml() {
      const bar = (w, h) => `<span class="answer-search__bone" style="--w: ${w}; --h: ${h}px"></span>`;

      return `
        <div class="answer-search__answer" data-sketch data-answer-bones>
          <div class="answer-search__skeleton-lines">${bar('62%', 18)}${bar('88%', 12)}${bar('54%', 12)}</div>
        </div>
      `;
    }

    /*
     * Hier stand die Korrekturzeile: das getippte Wort durchgestrichen daneben
     * und ein Link, doch nach dem Tippfehler zu suchen. Sie stand ueber jeder
     * Antwort, in der der Worker ein Wort gerade gezogen hatte - und erklaerte
     * einem Kunden seinen eigenen Vertipper, statt ihm sein Produkt zu zeigen.
     * Der Worker korrigiert weiter, er sagt es nur nicht mehr dazu.
     */

    /*
     * Die Tags des Modells standen hier einmal als Pillen unter dem Satz.
     *
     * Sie waren dekorativ - keine Zahl, kein Klick - und sagten daneben
     * dasselbe wie "Verfeinern", nur schlechter. Schlimmer noch: der Worker
     * setzt oft einen Filter vor, und dann versprach eine Pille "smoothie bowl"
     * ueber einer Liste, die auf Matcha eingegrenzt war und keine einzige Bowl
     * zeigte. Was die Liste eingrenzt, steht rechts, mit Zahlen.
     */
    answerHtml(data) {
      /* Statt der Rubrik das, was die Suche aus dem Satz gemacht hat - das ist
         die konkretere Auskunft und die Zeilen sind sie dafuer los. */
      const kicker = this.shared
        ? `${this.strings.understood || ''} ${this.shared}`.trim()
        : this.kickerFor(data.kind);

      return `
        <div class="answer-search__answer">
          <span class="answer-search__kicker">${this.escape(kicker)}</span>
          <h2 class="answer-search__headline">${this.escape(data.answer.headline)}</h2>
          <p class="answer-search__text">${this.escape(data.answer.body)}</p>
        </div>`;
    }

    kickerFor(kind) {
      const strings = this.strings;
      if (kind === 'question') return strings.answerQuestion || '';
      if (kind === 'statement') return strings.answerStatement || '';
      return strings.answerTerm || '';
    }

    chipsHtml(groups) {
      const chips = groups
        .map(
          (group) =>
            `<button class="answer-search__chip" type="button" data-group="${this.escape(group.value)}">
               ${this.escape(group.label)}
               <span class="answer-search__link-count">${group.count}</span>
             </button>`
        )
        .join('');
      return `<div class="answer-search__chips" data-group-row>${chips}</div>`;
    }

    moreHtml(rest, total) {
      const strings = this.strings;
      const label = (strings.more || '').replace('{{ count }}', rest);
      const all = (strings.total || '').replace('{{ count }}', total);
      return `
        <button class="answer-search__more" type="button" data-more>
          <span class="answer-search__more-label">${this.escape(label)}</span>
          <span class="answer-search__more-total">${this.escape(all)}</span>
        </button>`;
    }

    panelsHtml(data) {
      const strings = this.strings;
      const panels = [];

      if (data.facets?.length) {
        const rows = data.facets
          .map(
            (facet) =>
              `<button class="answer-search__facet" type="button" data-facet="${this.escape(facet.value)}"${
                facet.active ? ' data-active' : ''
              }>
                 <span class="answer-search__box-mark"></span>
                 <span class="answer-search__link-text">${this.escape(facet.label)}</span>
                 <span class="answer-search__link-count">${facet.count}</span>
               </button>`
          )
          .join('');
        panels.push(this.panelHtml(strings.refine, rows, null, 'data-refine'));
      }

      if (data.related?.length) {
        const chips = data.related
          .map(
            (term) =>
              `<button class="answer-search__chip" type="button" data-related="${this.escape(term)}">${this.escape(
                term
              )}</button>`
          )
          .join('');
        panels.push(this.panelHtml(strings.related, `<div class="answer-search__chips">${chips}</div>`));
      }


      return panels.join('');
    }

    panelHtml(title, body, count, marker) {
      return `
        <section class="answer-search__box"${marker ? ` ${marker}` : ''}>
          <div class="answer-search__box-head">
            <span class="answer-search__kicker">${this.escape(title || '')}</span>
            ${count ? `<span class="answer-search__count">${count}</span>` : ''}
          </div>
          ${body}
        </section>`;
    }

    renderSkeleton() {
      // The sketch replaces the dimmed list; dimming the sketch as well left
      // the whole loading state looking washed out.
      this.results.removeAttribute('data-loading');
      const strings = this.strings;
      const count = this.dataset.productCount || '';
      const bar = (w, h) => `<span class="answer-search__bone" style="--w: ${w}; --h: ${h}px"></span>`;
      const row = `
        <div class="answer-search__skeleton">
          <span class="answer-search__bone" style="--w: 96px; --h: 96px" data-square></span>
          <div class="answer-search__skeleton-info">
            ${bar('70px', 10)}${bar('46%', 16)}
            <div class="answer-search__skeleton-pills">${bar('96px', 20)}${bar('78px', 20)}${bar('64px', 20)}</div>
          </div>
          <div class="answer-search__skeleton-meta">${bar('72px', 18)}${bar('120px', 10)}${bar('104px', 12)}</div>
          <span class="answer-search__bone" style="--w: 190px; --h: 46px" data-button></span>
        </div>`;

      // The small ring the canvas puts before both status lines.
      const spinner = '<span class="answer-search__spinner" aria-hidden="true"></span>';

      this.results.innerHTML = `
        <div class="answer-search__answer" data-sketch>
          <div class="answer-search__skeleton-lines">${bar('62%', 18)}${bar('88%', 12)}${bar('54%', 12)}</div>
        </div>
        <div class="answer-search__list-head">
          <h2 class="answer-search__list-title">${this.escape(strings.products || '')}</h2>
          <span class="answer-search__note answer-search__status">${spinner}${this.escape((strings.searching || '').replace('{{ count }}', count))}</span>
        </div>
        <div class="answer-search__list">${row}${row}${row}</div>`;

      this.panels.innerHTML =
        this.panelHtml(strings.refine, `<div class="answer-search__skeleton-lines">${bar('78%', 14)}${bar('62%', 14)}${bar('70%', 14)}${bar('54%', 14)}</div>`) +
        this.panelHtml(strings.related, `<div class="answer-search__chips">${bar('150px', 34)}${bar('118px', 34)}${bar('134px', 34)}</div>`);
    }

    renderEmpty(query) {
      const strings = this.strings;
      this.results.innerHTML = `<p class="answer-search__note">${this.escape(
        (strings.empty || '').replace('{{ query }}', query)
      )}</p>`;
      this.panels.innerHTML = '';
    }

    /* ------------------------------------------------------------- recents */

    readRecent() {
      try {
        const raw = localStorage.getItem(AiAnswerSearch.RECENT_KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.filter((entry) => typeof entry === 'string') : [];
      } catch (error) {
        // Private windows and blocked site data throw on read. A missing history
        // is a missing box, not a broken search.
        return [];
      }
    }

    rememberQuery(query) {
      const term = query.trim();
      // A letter or two is a query on its way, not one worth offering again.
      if (term.length < AiAnswerSearch.WORKER_MIN_QUERY) return;

      const list = this.readRecent().filter((entry) => entry.toLowerCase() !== term.toLowerCase());
      list.unshift(term);

      try {
        localStorage.setItem(
          AiAnswerSearch.RECENT_KEY,
          JSON.stringify(list.slice(0, AiAnswerSearch.RECENT_MAX))
        );
      } catch (error) {
        /* nothing to do: the box simply stays as it was */
      }
    }

    renderRecent() {
      if (!this.recentBox || !this.recentList) return;

      const list = this.readRecent();
      this.recentBox.toggleAttribute('data-show', list.length > 0);
      this.recentList.innerHTML = list
        .map(
          (term) =>
            /*
             * Der Text in einem eigenen Element, nicht nackt in der Pille:
             * text-overflow greift nicht auf dem anonymen Flex-Item, das ein
             * blosser Textknoten in einem inline-flex-Container wird - der Satz
             * wurde dann links und rechts abgeschnitten statt hinten gekuerzt.
             */
            `<button class="answer-search__chip" type="button" data-related="${this.escape(term)}">
               <span class="answer-search__chip-text">${this.escape(term)}</span>
             </button>`
        )
        .join('');
    }

    /* -------------------------------------------------------------- events */

    handleOpenerClick(event) {
      const ziel = event.target instanceof Element ? event.target : null;

      /* Das Kreuz an der Leiste raeumt sie weg, statt die Suche zu oeffnen. */
      if (ziel?.closest('[data-answer-bar-close]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.dismissBar();
        return;
      }

      const opener = ziel ? ziel.closest('[data-answer-search-open]') : null;
      if (!opener) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(opener);
    }

    /**
     * Die Leiste im Header wegraeumen.
     *
     * Einmal weggeklickt, bleibt sie weg, auch auf spaeteren Besuchen
     * (localStorage). So abgestimmt mit Brekky: die Leiste soll auf jeder
     * Seite da sein, bis jemand sagt, dass er sie nicht will, und dann nicht
     * wieder aufploppen. Die Lupe im Header oeffnet die Suche weiterhin.
     * Versteckt wird beim Laden schon im Snippet, vor dem ersten Zeichnen.
     */
    dismissBar() {
      const bar = document.querySelector('[data-answer-bar]');
      if (bar) bar.hidden = true;

      try {
        localStorage.setItem(AiAnswerSearch.BAR_KEY, '1');
      } catch (error) {
        /* Privater Modus, geblockte Speicher: dann ist sie auf der naechsten
           Seite eben wieder da. */
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.hasAttribute('data-show')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Enter' && event.target === this.input && !event.isComposing) this.handleEnter(event);
    }

    /**
     * Enter im Feld, auf dem Telefon die Such-Taste der Tastatur.
     *
     * Die Suche startet nach der Tipp-Pause von selbst. Wartet sie noch, geht
     * sie jetzt sofort los. Ist dieselbe Frage schon beantwortet oder
     * unterwegs, wird sie nicht noch einmal gestellt: run() faengt von vorn an,
     * und die Liste verschwand und kam identisch wieder, auf dem Telefon
     * blitzte dabei die ganze Seite.
     *
     * Auf Touch-Geraeten verliert das Feld danach den Fokus. Dort ist die
     * Taste das "fertig", und ohne das blieb die Tastatur ueber den
     * Ergebnissen stehen und die Taste sah tot aus. Am Desktop bleibt der
     * Cursor, dort tippt man vielleicht weiter.
     */
    handleEnter(event) {
      event.preventDefault();

      const value = this.input.value.trim();
      const asked = value === this.asked && !this.timer;
      if (value.length >= AiAnswerSearch.MIN_QUERY && !asked) {
        this.query = value;
        this.cancel();
        this.run(value, { via: 'enter' });
      }

      if (window.matchMedia('(hover: none)').matches) this.input.blur();
    }

    handleClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const link = target.closest('a[href]');
      if (link) this.trackLink(link);

      const close = target.closest('[data-close]');
      if (close) return this.close();

      /*
       * Der Rest der Seite unter der Suche. Auf dem Desktop endet die Schicht
       * vor dem unteren Rand, und was darunter durchscheint, ist das
       * Pseudo-Element dieses Elements - ein Klick darauf trifft also das
       * Element selbst und nichts darin. Genau dann ist gemeint: zumachen.
       */
      if (target === this) return this.close();

      const clear = target.closest('[data-clear]');
      if (clear) {
        this.input.value = '';
        this.input.focus();
        this.handleInput();
        return;
      }

      const example = target.closest('[data-example]');
      if (example) return this.search(example.dataset.example, 'tile');

      const related = target.closest('[data-related]');
      if (related) return this.search(related.dataset.related, 'suggestion');

      const literal = target.closest('[data-literal]');
      if (literal) {
        // Deliberately the words as typed, and deliberately without the fix.
        this.input.value = literal.dataset.literal;
        this.query = literal.dataset.literal;
        this.toggleAttribute('data-typed', true);
        this.cancel();
        return void this.run(literal.dataset.literal, { correct: false, via: 'enter' });
      }

      const more = target.closest('[data-more]');
      if (more) return this.showRest(more);

      const group = target.closest('[data-group], [data-facet]');
      if (group) {
        event.preventDefault();
        return void this.toggleFilter(group.dataset.group || group.dataset.facet);
      }
    }

    search(query, via) {
      this.input.value = query;
      this.query = query;
      this.toggleAttribute('data-typed', true);
      this.input.focus();
      this.cancel();
      this.run(query, { via });
    }

    async showRest(button) {
      const list = this.results.querySelector('[data-answer-list]');
      if (!list || !this.remaining.length) return;

      button.toggleAttribute('data-busy', true);
      const controller = new AbortController();
      this.controller = controller;

      const rest = this.remaining;
      this.remaining = [];
      await this.appendRows(list, rest, controller.signal);
      if (!controller.signal.aborted) button.remove();
    }

    /**
     * Adding to the cart from a row, on the contract product-form.js uses: the
     * cart element names the sections it wants back, and hands the response to
     * its own renderer. Without JavaScript the form still posts and Shopify
     * still adds — this only keeps the shopper inside the layer.
     */
    async handleSubmit(event) {
      const form = event.target instanceof Element ? event.target.closest('[data-add-form]') : null;
      if (!form) return;

      event.preventDefault();

      const button = form.querySelector('[data-add]');
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      button?.toggleAttribute('data-busy', true);

      try {
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : { method: 'POST', headers: { Accept: 'application/javascript' } };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const body = new FormData(form);
        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.append('sections', cart.getSectionsToRender().map((section) => section.id));
          body.append('sections_url', window.location.pathname);
        }
        config.body = body;

        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}`, config);
        const parsed = await response.json();

        if (parsed.status) throw new Error(parsed.description || parsed.message);

        /* Erst nach dem Hinzufuegen: zwei Aenderungen am selben Warenkorb
           gleichzeitig verlieren gern eine davon. */
        this.trackRow('add', form);

        if (cart && typeof cart.renderContents === 'function') {
          // The drawer opens over the layer, so the layer steps aside — two
          // stacked overlays with two Escape meanings is one too many.
          this.close();
          cart.renderContents(parsed);
        }
      } catch (error) {
        console.warn('answer search: add to cart failed', error);
        form.submit();
      } finally {
        button?.toggleAttribute('data-busy', false);
      }
    }

    /* ----------------------------------------------------------- auswertung */

    track(type, detail) {
      answerTrack.send(this.dataset.endpoint, this.dataset.shop, type, detail);
    }

    /* Einmal je Frage, und erst wenn die Antwort eine Weile stehen bleibt. */
    trackShown(data) {
      const searchId = data.searchId;
      if (!searchId || searchId === this.shownSearchId) return;

      clearTimeout(this.seenTimer);
      this.seenTimer = setTimeout(() => {
        if (this.searchId !== searchId || this.shownSearchId === searchId) return;
        this.shownSearchId = searchId;
        this.track('shown', { sid: searchId, stage: 'final' });
      }, AiAnswerSearch.SEEN_AFTER_MS);
    }

    /* Wer klickt, hat die Antwort gesehen - auch wenn er schneller war als die Wartezeit oben. */
    markSeen() {
      if (!this.searchId || this.shownSearchId === this.searchId) return;
      clearTimeout(this.seenTimer);
      this.shownSearchId = this.searchId;
      this.track('shown', { sid: this.searchId, stage: 'final' });
    }

    trackRow(type, element) {
      const row = element.closest('[data-answer-row]');
      if (!row) return false;

      /* Die Beliebt-Zeilen stehen vor jeder Frage und gehoeren zu keiner. */
      if (row.closest('[data-popular]')) {
        if (type === 'click') this.track('popular', { handle: row.dataset.handle });
        return true;
      }

      const rows = [...this.results.querySelectorAll('[data-answer-list] [data-answer-row]')];
      const position = rows.indexOf(row) + 1 || null;
      this.markSeen();
      this.track(type, {
        sid: this.searchId,
        handle: row.dataset.handle,
        position,
        stage: 'final',
      });
      if (type === 'click') {
        answerTrack.follow(this.dataset.endpoint, this.dataset.shop, this.searchId, row.dataset.handle, position);
      }
      answerTrack.remember(this.searchId);
      return true;
    }

    trackLink(link) {
      this.trackRow('click', link);
    }

    /* Serviceanfragen beantwortet das Theme selbst. Getippt wird dabei weiter,
       also zaehlt derselbe Fall einmal. */
    trackLocal(query) {
      const key = query.toLowerCase();
      if (this.lastLocal && key.startsWith(this.lastLocal)) {
        this.lastLocal = key;
        return;
      }
      this.lastLocal = key;
      this.track('flag', { query });
    }

    escape(value) {
      const node = document.createElement('div');
      node.textContent = value == null ? '' : String(value);
      return node.innerHTML;
    }
  }

  customElements.define('ai-answer-search', AiAnswerSearch);
}
